// Palworld weapon / structure / gear / event-card guides.
// Each item ships a full guide article + dedicated FAQ article,
// SEO-optimized and Google Search Essentials compliant (visible
// content matches JSON-LD, self-referencing canonicals via route).
import type { Guide, ExternalLink } from "./guides-data";

const L = {
  fandom: { label: "Palworld Wiki (Fandom)", url: "https://palworld.fandom.com/wiki/Palworld_Wiki", note: "Community-maintained wiki." },
  fandomWeapons: { label: "Palworld Wiki — Weapons", url: "https://palworld.fandom.com/wiki/Weapons", note: "Full weapon stat tables." },
  fandomStructures: { label: "Palworld Wiki — Structures", url: "https://palworld.fandom.com/wiki/Structures", note: "Structure recipes and tiers." },
  fandomArmor: { label: "Palworld Wiki — Armor", url: "https://palworld.fandom.com/wiki/Armor", note: "Armor stats and recipes." },
  fandomItems: { label: "Palworld Wiki — Items", url: "https://palworld.fandom.com/wiki/Items", note: "Full items database." },
  pcgamer: { label: "PC Gamer — Palworld", url: "https://www.pcgamer.com/games/survival-crafting/palworld/", note: "PC Gamer guides and news." },
  ign: { label: "IGN — Palworld", url: "https://www.ign.com/games/palworld", note: "IGN Palworld hub." },
  gamerant: { label: "Game Rant — Palworld", url: "https://gamerant.com/tag/palworld/", note: "Game Rant guides." },
  polygon: { label: "Polygon — Palworld", url: "https://www.polygon.com/palworld", note: "Polygon feature coverage." },
  reddit: { label: "r/Palworld", url: "https://www.reddit.com/r/Palworld/", note: "Community sheets and patch discussion." },
  tcgOfficial: { label: "Palworld TCG — Official site", url: "https://palworldtcg.com/en/", note: "Official Palworld Card Game hub." },
  tcgWiki: { label: "Palworld TCG — Card database", url: "https://palworldtcg.gg/", note: "Community card database and search." },
} as const;

const pool = (a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] => [a, b, c];

interface ItemSpec {
  slug: string;         // final guide slug, e.g. "weapon-assault-rifle"
  title: string;        // Guide title (H1 on /guides/<slug>)
  desc: string;         // Meta description
  tag: string;          // Category chip
  keywords: string;     // Meta keywords (long-tail set)
  intro: string;        // Guide article intro
  sections: { heading: string; text: string }[]; // 4-6 sections
  faqIntro: string;
  faqs: { q: string; a: string }[]; // 5-7 Q&A
  links?: [ExternalLink, ExternalLink, ExternalLink];
}

function itemGuide(o: ItemSpec): Guide {
  const links = o.links ?? [L.fandom, L.pcgamer, L.gamerant];
  return {
    slug: o.slug,
    title: o.title,
    desc: o.desc,
    tag: o.tag,
    updated: "2026-07-29",
    keywords: o.keywords,
    articles: [
      {
        title: `${o.title} — full walkthrough`,
        slug: `${o.slug}-guide`,
        intro: o.intro,
        sections: o.sections,
        externalLinks: pool(links[0], links[1], links[2]),
      },
      {
        title: `${o.title} — FAQ`,
        slug: `${o.slug}-faq`,
        intro: o.faqIntro,
        sections: o.faqs.map((f) => ({ heading: f.q, text: f.a })),
        externalLinks: pool(L.reddit, links[1], links[2]),
      },
    ],
  };
}

// ---------- Weapons ----------
const WEAPONS: ItemSpec[] = [
  {
    slug: "weapon-wooden-club",
    title: "Wooden Club Guide — Palworld Weapon",
    desc: "Palworld Wooden Club stats, unlock level, crafting recipe and best use in the first hour.",
    tag: "Weapon",
    keywords: "palworld wooden club, wooden club recipe, palworld starter weapon, best early weapon palworld",
    intro: "The Wooden Club is the first melee weapon in Palworld and the fastest way to soften Lamballs for a guaranteed Pal Sphere catch. It unlocks at Level 2 from the Tech tree.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 2 for 1 Tech point. Crafted at the Primitive Workbench with 5 Wood. No Pal ingot required — buildable in the first two minutes." },
      { heading: "Base stats", text: "Damage 10, durability 100, one-handed swing. Deals blunt damage that reduces Pal HP without instant-killing — ideal for catching low-level Pals like Lamball, Chikipi and Cattiva." },
      { heading: "Best use", text: "Pair with Pal Spheres for the standard early catch loop: attack until the capture-rate ring turns green, then throw. Two-shots Lamball at Level 3." },
      { heading: "When to replace", text: "Swap to the Stone Spear at Level 6 for +5 damage and reach, then Bow at Level 8 for ranged catching. Keep one Wooden Club in a chest for melee-only shrine runs." },
    ],
    faqIntro: "Quick answers to the top-searched Wooden Club questions in Palworld.",
    faqs: [
      { q: "What level do you unlock the Wooden Club in Palworld?", a: "The Wooden Club unlocks at character Level 2 for 1 Tech point in the Primitive tab of the Technology tree." },
      { q: "How much damage does the Wooden Club do?", a: "The Wooden Club deals 10 base damage per swing with 100 durability. Damage scales lightly with your character attack stat." },
      { q: "Is the Wooden Club good for catching Pals?", a: "Yes — the Wooden Club is the recommended catching weapon for the first hour. Blunt melee damage softens Pals without one-shotting, which increases capture-rate odds." },
      { q: "What is the Wooden Club recipe in Palworld?", a: "5 Wood at the Primitive Workbench. No workbench upgrade or Pal skill required." },
      { q: "What replaces the Wooden Club later?", a: "The Stone Spear at Level 6, then the Bow at Level 8, then Metal Spear at Level 15. Assault Rifles enter at Level 34." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.gamerant],
  },
  {
    slug: "weapon-bow",
    title: "Bow Guide — Palworld Weapon",
    desc: "Palworld Bow stats, unlock, arrow crafting and headshot tips for early ranged combat.",
    tag: "Weapon",
    keywords: "palworld bow guide, palworld bow recipe, palworld bow arrows, palworld ranged weapon",
    intro: "The Bow is Palworld's first ranged weapon and the go-to catcher for the entire early game — it triggers headshot multipliers, doesn't spook Pals from range, and consumes cheap Wooden Arrows.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 8 for 1 Tech point. Crafted at the Workbench with 5 Wood and 5 Fiber. Wooden Arrows cost 1 Wood and 1 Stone each." },
      { heading: "Base stats", text: "Damage 15 per arrow (30 headshot), effective range ~35m, no reload delay between draws. Uses Wooden Arrows by default." },
      { heading: "Best use", text: "Pull mobs from ridges, aim for the head to trigger the ×2 crit, then close for the Pal Sphere throw. Excellent against Foxparks, Vixy and Rushoar." },
      { heading: "Ammo upgrades", text: "Craft Fire Arrows at Level 12 (+5 fire damage, ideal vs Grass Pals) and Poison Arrows at Level 20. Keep 30 Wooden + 20 elemental at all times." },
    ],
    faqIntro: "Common questions about the Bow and its arrow types in Palworld.",
    faqs: [
      { q: "What level do you unlock the Bow in Palworld?", a: "Level 8, 1 Tech point at the Workbench. The recipe is 5 Wood + 5 Fiber." },
      { q: "How do you craft arrows in Palworld?", a: "At the Workbench: Wooden Arrow needs 1 Wood + 1 Stone (bundle of 5). Fire Arrows unlock at Level 12 with 2 Wood + 1 Flame Organ." },
      { q: "Is the Bow good for catching Pals?", a: "Yes — the Bow is the best mid-game catching tool. Ranged shots avoid melee aggro and headshots proc a ×2 damage multiplier that softens Pals into the green capture ring quickly." },
      { q: "What is the Bow max damage in Palworld?", a: "15 body / 30 headshot with Wooden Arrows. Elemental arrows add 5 damage of matching type and can trigger burn or poison ticks." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.ign],
  },
  {
    slug: "weapon-crossbow",
    title: "Crossbow Guide — Palworld Weapon",
    desc: "Palworld Crossbow stats, unlock level, arrow types and best use vs Alpha Pals.",
    tag: "Weapon",
    keywords: "palworld crossbow guide, palworld crossbow recipe, crossbow vs bow palworld",
    intro: "The Crossbow trades draw speed for burst damage. It's the highest single-hit ranged option between the Bow and the Assault Rifle, and the best tool for softening Alpha Pals for capture.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 13 for 2 Tech points. Crafted with 20 Wood, 10 Ingot, 20 Fiber and 10 Nail at the Workbench." },
      { heading: "Base stats", text: "Damage 35 per bolt (70 headshot), reload ~2 seconds, effective range ~45m. Shares ammo with the Bow." },
      { heading: "Best use", text: "Alpha catching: pull from a cliff, land two headshots, throw a Mega Sphere at the green ring. Also excellent for sniping Chillet from mid-range." },
    ],
    faqIntro: "Crossbow FAQ — the most-asked questions for the Palworld Crossbow.",
    faqs: [
      { q: "What level unlocks the Crossbow in Palworld?", a: "Level 13, 2 Tech points, crafted at the standard Workbench." },
      { q: "Crossbow vs Bow in Palworld — which is better?", a: "Crossbow wins for burst DPS and Alpha softening; Bow wins for sustain and mobility. Carry both until you unlock the Assault Rifle at Level 34." },
      { q: "Can the Crossbow one-shot Pals?", a: "Yes — a headshot on low-level Pals like Lamball or Cattiva instantly kills them, so aim body-shots when catching is the goal." },
    ],
    links: [L.fandomWeapons, L.gamerant, L.polygon],
  },
  {
    slug: "weapon-handgun",
    title: "Old Bow to Handgun Transition Guide — Palworld",
    desc: "Palworld Handgun stats, recipe, ammo economy and when to switch from ranged bows.",
    tag: "Weapon",
    keywords: "palworld handgun, palworld handgun recipe, palworld pistol, palworld gunpowder",
    intro: "The Handgun is Palworld's first firearm and unlocks the ammunition economy — Handgun Ammo, Gunpowder and the High-Grade Workbench. It bridges the gap between the Crossbow and the Assault Rifle.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 21 for 2 Tech points. Crafted at the High-Grade Workbench with 20 Ingot, 20 Wood and 10 Cloth." },
      { heading: "Base stats", text: "Damage 50 per shot, 8-round magazine, effective range ~30m. Uses Handgun Ammo (1 Gunpowder + 1 Ingot per 5)." },
      { heading: "Best use", text: "Reliable body-shot DPS on Level 20-35 Pals. Excellent Tower boss opener — burst 8 rounds, roll, reload, repeat." },
      { heading: "Ammo loop", text: "Assign a Foxparks (Kindling) and a Digtoise (Mining) to keep a Charcoal + Sulfur pipeline for Gunpowder. Craft ammo in 100-round batches to save UI clicks." },
    ],
    faqIntro: "Everything players ask about the Palworld Handgun.",
    faqs: [
      { q: "What level do you unlock the Handgun in Palworld?", a: "Level 21, 2 Tech points, requires the High-Grade Workbench (Level 19)." },
      { q: "How do you make Gunpowder in Palworld?", a: "Gunpowder is crafted at the Production Assembly Line with 2 Charcoal + 1 Sulfur. Charcoal comes from cooking Wood; Sulfur is mined at desert nodes." },
      { q: "Is the Handgun worth using in Palworld?", a: "Yes for Level 20-30 gameplay and Tower openers. Once Assault Rifles unlock at Level 34, most players keep the Handgun as a backup sidearm." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.ign],
  },
  {
    slug: "weapon-assault-rifle",
    title: "Assault Rifle Guide — Palworld Weapon",
    desc: "Palworld Assault Rifle stats, recipe, DPS and best build for Tower and Raid bosses.",
    tag: "Weapon",
    keywords: "palworld assault rifle, palworld assault rifle recipe, best palworld weapon, palworld tower boss weapon",
    intro: "The Assault Rifle is the meta workhorse of mid-to-late Palworld — 30-round mags, high DPS, and it carries you from Tower 3 all the way to Jetragon.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 34 for 3 Tech points. Requires 50 Refined Ingot, 20 Polymer, 30 Carbon Fiber and 20 Cloth at the Production Assembly Line." },
      { heading: "Base stats", text: "Damage 65 per round, 30-round magazine, RPM ~500, effective range ~50m. Uses Assault Rifle Ammo (5 Gunpowder + 1 Refined Ingot per 5)." },
      { heading: "Best use", text: "Boss fights: strafe left, hip-fire full mag, roll, reload. Sustained DPS is highest of any non-heavy weapon before Rocket Launcher." },
      { heading: "Ammo economy", text: "Automate at the Production Assembly Line with 4 Anubis on High Handiwork. Stack Refined Ingot at a Furnace with 2 Blazamut for constant flow." },
    ],
    faqIntro: "Answers to the most-searched Assault Rifle questions in Palworld 1.0.",
    faqs: [
      { q: "What level unlocks the Assault Rifle in Palworld?", a: "Level 34, 3 Tech points, at the Production Assembly Line. You also need the High-Grade Workbench chain unlocked." },
      { q: "How much damage does the Assault Rifle do?", a: "65 damage per round at 500 RPM — roughly 540 DPS sustained before headshots and elemental multipliers." },
      { q: "Assault Rifle vs Rocket Launcher in Palworld?", a: "Assault Rifle wins for sustained DPS and ammo economy; Rocket Launcher wins for burst on Alpha and Tower bosses. Most players carry both by Level 40." },
      { q: "Best ammo for Assault Rifle?", a: "Assault Rifle Ammo is the default. There is no elemental variant — pair with an elemental Pal (Jetragon, Blazamut, Frostallion) for element coverage." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.gamerant],
  },
  {
    slug: "weapon-pump-shotgun",
    title: "Pump Shotgun Guide — Palworld Weapon",
    desc: "Palworld Pump-Action Shotgun stats, unlock and best use for melee-range boss DPS.",
    tag: "Weapon",
    keywords: "palworld shotgun, palworld pump shotgun, palworld shotgun recipe, palworld close range weapon",
    intro: "The Pump-Action Shotgun is Palworld's highest per-shot burst weapon inside 10 meters. It shreds Tower boss shields and one-shots most non-Alpha Pals point-blank.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 30 for 2 Tech points. Recipe: 40 Ingot, 20 Wood, 10 Cloth, 5 Nail at the High-Grade Workbench." },
      { heading: "Base stats", text: "8 pellets × 15 damage = 120 damage per shell inside 10m, 5-round tube, ~1.2s pump. Uses Shotgun Shells (2 Gunpowder + 2 Ingot per 4)." },
      { heading: "Best use", text: "Zoe & Grizzbolt Tower opener, Bushi camp clears, and any Alpha capture where you can safely close the gap." },
    ],
    faqIntro: "Common Pump Shotgun questions.",
    faqs: [
      { q: "What level unlocks the Shotgun in Palworld?", a: "Level 30, 2 Tech points, at the High-Grade Workbench." },
      { q: "Shotgun DPS vs Assault Rifle DPS in Palworld?", a: "Shotgun wins burst inside 10m (~120/shell). Assault Rifle wins sustained (~540 DPS). Carry both for Tower runs." },
      { q: "Best ammo for Shotgun in Palworld?", a: "Standard Shotgun Shells. No elemental variant exists — pair the shotgun with an elemental partner Pal for coverage." },
    ],
    links: [L.fandomWeapons, L.gamerant, L.polygon],
  },
  {
    slug: "weapon-rocket-launcher",
    title: "Rocket Launcher Guide — Palworld Weapon",
    desc: "Palworld Rocket Launcher stats, unlock, best build and how to farm Jetragon.",
    tag: "Weapon",
    keywords: "palworld rocket launcher, palworld rocket launcher recipe, palworld jetragon weapon, best boss weapon palworld",
    intro: "The Rocket Launcher is Palworld's end-game siege weapon and the fastest way to solo Jetragon. It costs a fortune per shot but each Rocket deals splash damage that ignores most Alpha armor.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 48 for 5 Tech points. Recipe: 100 Refined Ingot, 30 Polymer, 20 Carbon Fiber at the Production Assembly Line." },
      { heading: "Base stats", text: "Damage 900 per rocket + 3m splash. Single-shot, ~3s reload. Uses Rockets (10 Gunpowder + 5 Refined Ingot each)." },
      { heading: "Best use", text: "Legendary boss farm: Jetragon, Frostallion, Necromus and Paladius all die in 6-9 rockets from cover." },
    ],
    faqIntro: "Rocket Launcher FAQ.",
    faqs: [
      { q: "What level unlocks the Rocket Launcher in Palworld?", a: "Level 48, 5 Tech points at the Production Assembly Line. Requires 100 Refined Ingot to craft." },
      { q: "How much damage does the Rocket Launcher do?", a: "900 direct hit + splash. Kills most Alphas in 2-3 rockets and Jetragon in ~8 rockets." },
      { q: "How do you farm Rockets in Palworld?", a: "Stack Sulfur mining with 2 Digtoise, Charcoal from Foxparks Kindling, and Refined Ingot from Improved Furnaces. Craft in batches of 20." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.ign],
  },
  {
    slug: "weapon-grenade",
    title: "Grenade Guide — Palworld Weapon",
    desc: "Palworld Grenade recipe, damage, unlock level and how to farm Syndicate camps.",
    tag: "Weapon",
    keywords: "palworld grenade, palworld grenade recipe, palworld throwable weapon",
    intro: "The Grenade is Palworld's cheapest area-damage tool — perfect for clearing Syndicate camps and softening groups of low-level Pals for mass capture.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 21 for 1 Tech point. Recipe: 5 Gunpowder + 5 Ingot per grenade at the Workbench." },
      { heading: "Base stats", text: "Damage 350 in a 4m radius, 3-second fuse, throw arc ~15m. Friendly-fire capable — do not throw next to your own Pals." },
      { heading: "Best use", text: "Syndicate Tower camps: cook the grenade 1 second, then bank it into the crate near the guards for a 3-kill wipe." },
    ],
    faqIntro: "Grenade FAQ.",
    faqs: [
      { q: "What level unlocks Grenades in Palworld?", a: "Level 21, 1 Tech point at the standard Workbench." },
      { q: "Grenade radius in Palworld?", a: "4-meter blast radius, 350 damage at center falling off to ~120 at the edge." },
      { q: "Do Grenades hurt your own Pals?", a: "Yes. Grenades apply full friendly-fire damage — recall Pals with the whistle before throwing." },
    ],
    links: [L.fandomWeapons, L.gamerant, L.polygon],
  },
  {
    slug: "weapon-flamethrower",
    title: "Flamethrower Guide — Palworld Weapon",
    desc: "Palworld Flamethrower stats, fuel, recipe and best use vs Ice Pals like Frostallion.",
    tag: "Weapon",
    keywords: "palworld flamethrower, palworld flamethrower recipe, palworld fire weapon, frostallion counter",
    intro: "The Flamethrower is Palworld's mid-range sustained fire-type weapon. Its burn tick makes it the fastest way to melt Ice Pals like Frostallion and Reindrix.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 26 for 3 Tech points. Recipe: 30 Ingot, 20 Wood, 15 Cloth at the High-Grade Workbench." },
      { heading: "Base stats", text: "Damage 25/second stream + 5/second burn for 5 seconds. Fuel: 1 unit per 0.1s stream. Fuel Tank refills at any workbench with Flammable Fluid." },
      { heading: "Best use", text: "Frostallion arena at (-378, -534) — kite around the pillar and hold the trigger; Frostallion melts in ~40 seconds." },
    ],
    faqIntro: "Flamethrower FAQ.",
    faqs: [
      { q: "What level unlocks the Flamethrower in Palworld?", a: "Level 26, 3 Tech points at the High-Grade Workbench." },
      { q: "How do you refuel the Flamethrower?", a: "Craft Flammable Fluid at the Production Assembly Line (Sulfur + Wood) and reload at any workbench." },
      { q: "Is the Flamethrower good vs Frostallion?", a: "Yes — fire damage is ×2 effective against ice. Flamethrower is often the fastest solo kill for the Frostallion Legendary boss." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.ign],
  },
  {
    slug: "weapon-legendary-assault-rifle",
    title: "Legendary Assault Rifle Guide — Palworld Weapon",
    desc: "Palworld Legendary Assault Rifle Schematic drops, stats and best boss build.",
    tag: "Weapon",
    keywords: "palworld legendary assault rifle, palworld legendary schematic, palworld best weapon",
    intro: "The Legendary Assault Rifle is the strongest sustained-fire weapon in Palworld 1.0. It drops as a Schematic from Level 50+ Alphas and outputs ~40% more DPS than a standard AR.",
    sections: [
      { heading: "How to unlock", text: "Legendary Assault Rifle Schematic drops from Blazamut (Volcano Alpha), Jetragon (dune arena) and Necromus. Craft the weapon at Production Assembly Line with 100 Refined Ingot, 40 Polymer, 30 Carbon Fiber." },
      { heading: "Base stats", text: "Damage 91 per round, 30 mag, ~500 RPM. Sustained DPS ≈ 758 — the top pre-Rocket Launcher output." },
      { heading: "Best use", text: "Solo Tower and Alpha farming: stack Ferocious + Musclehead on a Frostallion mount, hop off, empty a mag, remount and repeat." },
    ],
    faqIntro: "Legendary AR FAQ.",
    faqs: [
      { q: "How do you get the Legendary Assault Rifle Schematic in Palworld?", a: "It drops from Level 50+ Alpha Pals — Blazamut, Jetragon, Necromus and the Tower bosses on higher difficulty. Drop rate is ~2%." },
      { q: "Legendary AR vs standard AR in Palworld?", a: "Legendary AR does 91 damage per round vs 65 for the standard AR — about a 40% DPS uplift with identical ammo cost." },
      { q: "Best Passives for Legendary AR user in Palworld?", a: "Stack Legend, Musclehead, Ferocious and Lucky on your character's active Pal to buff outgoing damage; the weapon itself has no perks." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.gamerant],
  },
  {
    slug: "weapon-musket",
    title: "Old Musket Guide — Palworld Weapon",
    desc: "Palworld Old Musket stats, recipe, unlock level and when to switch to Assault Rifle.",
    tag: "Weapon",
    keywords: "palworld musket, palworld old musket, palworld single-shot rifle",
    intro: "The Old Musket is Palworld's introductory firearm — a slow, hard-hitting single-shot rifle that bridges the Handgun and Assault Rifle in the mid-game.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 27 for 2 Tech points. Recipe: 30 Ingot, 30 Wood, 20 Cloth at the High-Grade Workbench." },
      { heading: "Base stats", text: "Damage 110 per shot (220 headshot), single-round chamber, ~2.5s reload, effective range ~60m. Uses Musket Bullets (2 Gunpowder + 1 Ingot per bullet)." },
      { heading: "Best use", text: "Long-range Alpha softening. Headshot a Chillet at 55m to trigger a 220-damage crit, then throw a Mega Sphere while it's stunned." },
    ],
    faqIntro: "Old Musket FAQ.",
    faqs: [
      { q: "What level is the Old Musket in Palworld?", a: "Level 27, 2 Tech points at the High-Grade Workbench." },
      { q: "Musket vs Handgun in Palworld?", a: "Musket has higher per-shot damage and range; Handgun has faster fire rate and cheaper ammo. Keep Musket for openers, Handgun for follow-up." },
      { q: "When should you replace the Old Musket?", a: "At Level 34 when the Assault Rifle unlocks. Musket becomes redundant once sustained DPS matters more than burst." },
    ],
    links: [L.fandomWeapons, L.gamerant, L.polygon],
  },
  {
    slug: "weapon-pal-launcher",
    title: "Giga Pal Launcher Guide — Palworld Weapon",
    desc: "Palworld Giga Pal Launcher stats, recipe and how it changes catch odds.",
    tag: "Weapon",
    keywords: "palworld pal launcher, palworld giga pal launcher, palworld capture weapon",
    intro: "The Giga Pal Launcher fires Pal Spheres from long range with a bonus capture-rate multiplier. It's the end-game shiny-hunter's best friend.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 44 for 3 Tech points. Recipe: 80 Refined Ingot, 20 Polymer, 15 Circuit Board at the Production Assembly Line." },
      { heading: "Base stats", text: "Ranged sphere-throw ~45m. Flat +30% capture rate on top of the sphere's own multiplier. Loads any tier from Pal to Legendary Sphere." },
      { heading: "Best use", text: "Lucky-hunt Legendaries from a cliff without triggering aggression. Also lets you finish Jetragon captures with one Legendary Sphere at 40m." },
    ],
    faqIntro: "Giga Pal Launcher FAQ.",
    faqs: [
      { q: "What level unlocks the Giga Pal Launcher in Palworld?", a: "Level 44, 3 Tech points at the Production Assembly Line." },
      { q: "Does the Pal Launcher improve capture rates?", a: "Yes — the Giga Pal Launcher applies a +30% catch-rate multiplier to whichever sphere you load." },
      { q: "Can you use Legendary Spheres in the Giga Pal Launcher?", a: "Yes. Every sphere tier is compatible, including Ultra and Legendary Spheres." },
    ],
    links: [L.fandomWeapons, L.pcgamer, L.reddit],
  },
];

// ---------- Structures ----------
const STRUCTURES: ItemSpec[] = [
  {
    slug: "structure-palbox",
    title: "Palbox Guide — Palworld Structure",
    desc: "Palworld Palbox recipe, unlock, base radius and multi-base management tips.",
    tag: "Structure",
    keywords: "palworld palbox, palworld base, palworld base radius, palworld multiple bases",
    intro: "The Palbox is the anchor of every Palworld base. It defines the base radius, stores Pals, and lets you fast-travel between the three bases the game allows.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 1 (free). Recipe: 8 Wood, 3 Stone, 1 Paldium Fragment. Craftable from the Build menu with no workbench required." },
      { heading: "Base radius", text: "The Palbox sets a large cylindrical build zone. Pals inside the zone automatically work on assigned tasks; Pals outside idle." },
      { heading: "Multi-base cap", text: "You can place up to 3 Palboxes across the map. Use one for farming, one for smelting/ammo, one for breeding to avoid worker Pal collisions." },
    ],
    faqIntro: "Palbox FAQ.",
    faqs: [
      { q: "How do you unlock the Palbox in Palworld?", a: "The Palbox is unlocked from the start of the game at Level 1. It costs 8 Wood, 3 Stone and 1 Paldium Fragment." },
      { q: "How many bases can you have in Palworld?", a: "Up to 3 active Palboxes at once. Deconstruct one Palbox to place a fourth elsewhere." },
      { q: "Do Pals work outside the Palbox radius?", a: "No — Pals only auto-work when assigned to a base and standing inside its Palbox radius. Pals outside the radius idle or wander." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-primitive-workbench",
    title: "Primitive Workbench Guide — Palworld Structure",
    desc: "Palworld Primitive Workbench recipe, upgrades and best worker Pals.",
    tag: "Structure",
    keywords: "palworld primitive workbench, palworld workbench upgrade, palworld handiwork pal",
    intro: "The Primitive Workbench is the first crafting station in Palworld. Assign a Cattiva or Lifmunk with Handiwork to auto-craft while you explore.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 2, 1 Tech point. Recipe: 2 Wood." },
      { heading: "Upgrades", text: "Upgrade to Workbench at Level 8, High-Grade Workbench at Level 19, Production Assembly Line at Level 32. Each upgrade unlocks recipe tiers." },
      { heading: "Best workers", text: "Handiwork 1: Cattiva, Lifmunk. Handiwork 2: Tanzee, Vixy. Handiwork 3-4: Anubis (best-in-slot for late-game)." },
    ],
    faqIntro: "Primitive Workbench FAQ.",
    faqs: [
      { q: "What is the Primitive Workbench recipe in Palworld?", a: "2 Wood, unlocked at Level 2 for 1 Tech point." },
      { q: "Best Pals for the Workbench in Palworld?", a: "Anubis (Handiwork 4) is the best-in-slot. Early game use Cattiva or Lifmunk with Handiwork 1." },
      { q: "How do you upgrade the Workbench?", a: "Unlock the next-tier bench in the Tech tree and place it as a separate structure. The old Workbench can be deconstructed after." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-cooking-pot",
    title: "Cooking Pot Guide — Palworld Structure",
    desc: "Palworld Cooking Pot recipes, unlock, cake production and best chef Pals.",
    tag: "Structure",
    keywords: "palworld cooking pot, palworld cake recipe, palworld cooking pals",
    intro: "The Cooking Pot turns raw ingredients into food, medicine base and — critically — the Cakes that power the Breeding Farm.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 4, 1 Tech point. Recipe: 15 Wood, 3 Stone, 3 Ingot." },
      { heading: "Key recipes", text: "Cake (5 Flour + 8 Red Berries + 7 Milk + 8 Eggs + 2 Honey), Pancake, Grilled Meat, Salad, Baked Berries. Cake is the only structure output that feeds the Breeding Farm." },
      { heading: "Best chefs", text: "Assign Kindling + Handiwork Pals. Wixen (Kindling 3) or Foxparks (Kindling 1) start fires; Cattiva loads ingredients." },
    ],
    faqIntro: "Cooking Pot FAQ.",
    faqs: [
      { q: "What is the Cooking Pot recipe in Palworld?", a: "15 Wood + 3 Stone + 3 Ingot at Level 4 for 1 Tech point." },
      { q: "How do you make a Cake in Palworld?", a: "At the Cooking Pot: 5 Flour + 8 Red Berries + 7 Milk + 8 Eggs + 2 Honey. Requires Mozzarina (Milk), Chikipi (Eggs) and Beegarde (Honey) on a Ranch." },
      { q: "Best cooking Pals in Palworld?", a: "Wixen (Kindling 3) and Blazehowl (Kindling 3) auto-light the pot. Add a Cattiva for Handiwork to feed ingredients." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.ign],
  },
  {
    slug: "structure-breeding-farm",
    title: "Breeding Farm Guide — Palworld Structure",
    desc: "Palworld Breeding Farm recipe, cake requirement and passive-skill breeding loop.",
    tag: "Structure",
    keywords: "palworld breeding farm, palworld breeding pen, palworld egg incubator",
    intro: "The Breeding Farm pairs two Pals into an egg. Fed a Cake and given time, it produces the offspring your Breed Power math predicts.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 19, 3 Tech points. Recipe: 100 Wood, 20 Stone, 50 Fiber." },
      { heading: "How it works", text: "Place one male + one female Pal at the Farm, then drop 1 Cake into the feed box. After 5 minutes an egg spawns; incubate it in an Egg Incubator." },
      { heading: "Breed Power", text: "Child = Pal whose Breed Power is closest to floor((Parent A + Parent B + 1)/2). Passive skills roll separately, weighted by parent tier." },
    ],
    faqIntro: "Breeding Farm FAQ.",
    faqs: [
      { q: "What level unlocks the Breeding Farm in Palworld?", a: "Level 19, 3 Tech points, 100 Wood + 20 Stone + 50 Fiber." },
      { q: "How long does breeding take in Palworld?", a: "Egg spawns after ~5 minutes with cake fed. Hatch times range from 10 minutes (Common) to 2h30m (Legendary) on default settings." },
      { q: "How many Breeding Farms can you have?", a: "One per base (3 total across all Palboxes). Each Farm handles one pair at a time." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-egg-incubator",
    title: "Egg Incubator Guide — Palworld Structure",
    desc: "Palworld Egg Incubator recipe, temperature tips and hatch-time reduction.",
    tag: "Structure",
    keywords: "palworld egg incubator, palworld hatch eggs, palworld incubator temperature",
    intro: "The Egg Incubator hatches every egg you find or breed. Correct temperature drops hatch time dramatically — critical for passive-skill rerolls.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 7, 2 Tech points. Recipe: 10 Paldium Fragment, 30 Stone, 5 Cloth, 2 Ancient Civilization Parts (drops from Alpha Pals)." },
      { heading: "Temperature tuning", text: "Each egg type prefers a temperature. Damp Eggs like cool (place near Vanwyrm Cryst), Scorching Eggs like heat (place near a Campfire or Foxparks)." },
      { heading: "Speed tricks", text: "Reduce hatch time in world settings to 30 minutes for faster passive rerolls. Legendary eggs still deliver the highest-tier passives after the reroll grind." },
    ],
    faqIntro: "Egg Incubator FAQ.",
    faqs: [
      { q: "What are Ancient Civilization Parts for in Palworld?", a: "They're the key ingredient for the Egg Incubator and several late-game structures. They drop from Alphas and Syndicate lieutenants." },
      { q: "How do you speed up hatch times in Palworld?", a: "Change 'Egg Hatching Speed' in world settings (30 min recommended) and match the incubator's temperature to the egg type." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.ign],
  },
  {
    slug: "structure-electric-furnace",
    title: "Electric Furnace Guide — Palworld Structure",
    desc: "Palworld Electric Furnace recipe, unlock and how to mass-produce Refined Ingot.",
    tag: "Structure",
    keywords: "palworld electric furnace, palworld refined ingot, palworld mass smelting",
    intro: "The Electric Furnace is Palworld's end-game smelter. It skips the Kindling worker requirement and mass-produces Refined Ingots for Assault Rifle ammo and Legendary weapons.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 34, 3 Tech points. Recipe: 20 Refined Ingot, 30 Ingot, 20 Cement, 5 Circuit Board, 10 Polymer." },
      { heading: "Best workers", text: "Electric Pals: Grizzbolt (Electricity 2), Orserk (Electricity 3, Handiwork 3), Beakon (Electricity 3). Skip Foxparks — no Kindling needed." },
      { heading: "Throughput", text: "~4× the Improved Furnace with two Orserk assigned. Pair with Blazamut on a partner Furnace for peak Refined Ingot output." },
    ],
    faqIntro: "Electric Furnace FAQ.",
    faqs: [
      { q: "What level unlocks the Electric Furnace in Palworld?", a: "Level 34, 3 Tech points at the Production Assembly Line tier." },
      { q: "Best Pal for the Electric Furnace?", a: "Orserk (Electricity 3, Handiwork 3) is best-in-slot. Grizzbolt and Beakon are strong runner-ups." },
      { q: "Does the Electric Furnace need Coal or Wood?", a: "No fuel needed — the Electric Furnace runs entirely on assigned Electricity worker Pals." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-production-assembly-line",
    title: "Production Assembly Line Guide — Palworld Structure",
    desc: "Palworld Production Assembly Line recipe, upgrades and best worker Pals.",
    tag: "Structure",
    keywords: "palworld assault rifle crafting, palworld ammo crafting, production assembly line structure",
    intro: "The Production Assembly Line is Palworld's late-game crafting bench. It's required for Assault Rifle, Rocket Launcher, Polymer and every end-game Schematic weapon.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 32, 3 Tech points. Recipe: 50 Ingot, 20 Nail, 20 Cloth, 20 Cement." },
      { heading: "Upgrade to Advanced", text: "Advanced Assembly Line unlocks at Level 44 and doubles crafting speed. Recipe includes Circuit Boards and Polymer." },
      { heading: "Best workers", text: "Anubis (Handiwork 4) is the top pick. Stack 4 Anubis + 1 Orserk for constant throughput on Assault Rifle Ammo." },
    ],
    faqIntro: "Production Assembly Line FAQ.",
    faqs: [
      { q: "What level unlocks the Production Assembly Line in Palworld?", a: "Level 32, 3 Tech points. Requires the High-Grade Workbench already unlocked." },
      { q: "Best Pals for the Assembly Line?", a: "Anubis (Handiwork 4). Four of them fill the workstation and keep an ammo pipeline running while you're offline." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.ign],
  },
  {
    slug: "structure-berry-plantation",
    title: "Berry Plantation Guide — Palworld Structure",
    desc: "Palworld Berry Plantation recipe, unlock and best worker Pals for auto-food.",
    tag: "Structure",
    keywords: "palworld berry plantation, palworld farming, palworld food supply",
    intro: "The Berry Plantation is the first automated food source in Palworld. Assign a Watering + Planting + Gathering trio to keep a full food stack in your Feed Box.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 5, 2 Tech points. Recipe: 20 Wood, 20 Stone, 3 Berry Seeds." },
      { heading: "Worker chain", text: "Planting (Lifmunk / Verdash), Watering (Pengullet / Fuack), Gathering (Cattiva / Vixy). Auto-loop feeds base Pals hands-free." },
    ],
    faqIntro: "Berry Plantation FAQ.",
    faqs: [
      { q: "How many Berry Plantations do you need in Palworld?", a: "Two per base of 10 Pals is plenty. Add a Wheat Plantation once you unlock Cakes at Level 17." },
      { q: "Best Pals for the Berry Plantation?", a: "Lifmunk (Planting 1) + Pengullet (Watering 1) + Cattiva (Gathering 1) covers the full loop before Level 10." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-hot-spring",
    title: "Hot Spring Guide — Palworld Structure",
    desc: "Palworld Hot Spring recipe, SAN recovery rate and best base placement.",
    tag: "Structure",
    keywords: "palworld hot spring, palworld san recovery, palworld pal sanity",
    intro: "The Hot Spring restores Pal SAN (sanity) at the highest rate of any structure. Every mid-to-late base should have one to prevent depression and work refusal.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 14, 2 Tech points. Recipe: 50 Stone, 20 Wood, 3 Paldium Fragment." },
      { heading: "SAN recovery", text: "+50 SAN over the bathing cycle (~90 seconds). Two bathers at once — build two Hot Springs for a base with 10+ Pals." },
    ],
    faqIntro: "Hot Spring FAQ.",
    faqs: [
      { q: "How do you fix low SAN in Palworld?", a: "Build a Hot Spring, medicine (Low-Grade Medical Supplies) and beds. Hot Springs deliver the fastest passive SAN restore in the game." },
      { q: "How many Hot Springs do you need?", a: "One per 5 base Pals. Bathing capacity is 2 Pals per Hot Spring at a time." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
  {
    slug: "structure-feed-box",
    title: "Feed Box Guide — Palworld Structure",
    desc: "Palworld Feed Box recipe, unlock and stocking tips to prevent hunger.",
    tag: "Structure",
    keywords: "palworld feed box, palworld hungry pals, palworld pal food",
    intro: "The Feed Box holds food for every Pal assigned to your base. An empty Feed Box drops SAN fast and eventually triggers work refusal.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 4, 1 Tech point. Recipe: 20 Wood." },
      { heading: "Stocking tips", text: "Keep 30+ Red Berries in early game, 50+ Baked Berries by Level 15, and 20+ Salad by Level 30. Fill from the storage chest, not by hand." },
    ],
    faqIntro: "Feed Box FAQ.",
    faqs: [
      { q: "Why are my Pals not working in Palworld?", a: "Nine times out of ten it's an empty Feed Box or missing bed. Restock food and place beds inside the base radius." },
      { q: "How much food should be in a Feed Box?", a: "Aim for ~5 food items per assigned Pal. A base of 10 Pals should always have 50+ Baked Berries or higher-tier cooked meat." },
    ],
    links: [L.fandomStructures, L.pcgamer, L.gamerant],
  },
];

// ---------- Gear (armor / accessories) ----------
const GEAR: ItemSpec[] = [
  {
    slug: "gear-cloth-outfit",
    title: "Cloth Outfit Guide — Palworld Gear",
    desc: "Palworld Cloth Outfit recipe, defense and when to upgrade to Leather.",
    tag: "Gear",
    keywords: "palworld cloth outfit, palworld starter armor, palworld first armor",
    intro: "The Cloth Outfit is Palworld's first body armor. It bumps your defense to 20 and shrugs off cold nights in the Windswept Hills.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 4, 1 Tech point. Recipe: 4 Cloth at the Workbench (Cloth is crafted from 2 Wool at the Primitive Workbench)." },
      { heading: "Stats", text: "Defense 20, minor cold resist. Wool comes from Lamball (auto-farm at Ranch)." },
      { heading: "When to upgrade", text: "Swap to Leather Armor at Level 12 (Def 60). Cloth is redundant by Level 15." },
    ],
    faqIntro: "Cloth Outfit FAQ.",
    faqs: [
      { q: "How do you make Cloth in Palworld?", a: "2 Wool → 1 Cloth at the Primitive Workbench. Wool comes from a Lamball assigned to a Ranch." },
      { q: "Is the Cloth Outfit worth crafting?", a: "Yes for Levels 4-11. It's the cheapest way to survive night one and adds a bit of cold resist." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-leather-armor",
    title: "Leather Armor Guide — Palworld Gear",
    desc: "Palworld Leather Armor recipe, defense and best usage window.",
    tag: "Gear",
    keywords: "palworld leather armor, palworld mid armor, palworld leather recipe",
    intro: "Leather Armor triples the Cloth Outfit's defense and unlocks the Metal armor tier. It's the standard mid-game body slot from Level 12 to Level 21.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 12, 2 Tech points. Recipe: 6 Leather + 6 Cloth at the Workbench. Leather drops from Rushoar and Direhowl." },
      { heading: "Stats", text: "Defense 60, no elemental resist bonus." },
      { heading: "Upgrade path", text: "Metal Armor at Level 21 (Def 130). Leather is best replaced by then." },
    ],
    faqIntro: "Leather Armor FAQ.",
    faqs: [
      { q: "Where does Leather drop in Palworld?", a: "Rushoar, Direhowl and Eikthyrdeer are the top Leather droppers. Kill them or send a hunting Pal to auto-farm." },
      { q: "Leather Armor stats in Palworld?", a: "Defense 60, no elemental resist. Bridges Cloth (20 Def) to Metal (130 Def)." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-metal-armor",
    title: "Metal Armor Guide — Palworld Gear",
    desc: "Palworld Metal Armor recipe, defense and where to farm Ingot fast.",
    tag: "Gear",
    keywords: "palworld metal armor, palworld ingot farming, palworld mid armor",
    intro: "Metal Armor is the go-to mid-tier body slot. Defense 130 lets you tank Chillet claws and Bushi katanas without dying.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 21, 2 Tech points. Recipe: 20 Ingot + 6 Cloth + 3 Leather at the Workbench." },
      { heading: "Upgrade path", text: "Refined Metal Armor at Level 30 (Def 220), Pal Metal Armor at Level 38 (Def 330)." },
    ],
    faqIntro: "Metal Armor FAQ.",
    faqs: [
      { q: "How much defense does Metal Armor give in Palworld?", a: "130 flat defense. Refined Metal Armor jumps to 220 at Level 30." },
      { q: "How do you farm Ingot for Metal Armor?", a: "Automate a Furnace with Foxparks (Kindling) and Digtoise (Mining). One base can output 200+ Ingot per hour." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-pal-metal-armor",
    title: "Pal Metal Armor Guide — Palworld Gear",
    desc: "Palworld Pal Metal Armor recipe, unlock level and best pairing with elemental resist.",
    tag: "Gear",
    keywords: "palworld pal metal armor, palworld best armor, palworld endgame armor",
    intro: "Pal Metal Armor is Palworld's top standard body slot before Legendary Schematics. Defense 330 survives Jetragon rockets when paired with cold-resist accessories.",
    sections: [
      { heading: "Unlock and recipe", text: "Unlocks at Level 38, 3 Tech points. Recipe: 20 Pal Metal Ingot + 6 Cloth + 3 Leather at the Production Assembly Line." },
      { heading: "Legendary variant", text: "Legendary Schematics drop from Volcano Alpha and boost defense to 500. Farm Blazamut for a 2% Schematic drop." },
    ],
    faqIntro: "Pal Metal Armor FAQ.",
    faqs: [
      { q: "What is Pal Metal Ingot in Palworld?", a: "A refined ingot crafted from 2 Ingot + 1 Paldium Fragment at the Improved Furnace. Required for all end-game armor." },
      { q: "Is Pal Metal Armor the best armor in Palworld?", a: "It's the best non-Schematic tier. Legendary Pal Metal Armor (Schematic-crafted) is the true best-in-slot at Def 500." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.ign],
  },
  {
    slug: "gear-heat-resistant-outfit",
    title: "Heat Resistant Outfit Guide — Palworld Gear",
    desc: "Palworld Heat Resistant Outfit recipe and how to survive the Volcano biome.",
    tag: "Gear",
    keywords: "palworld heat resistant outfit, palworld volcano armor, palworld hot biome armor",
    intro: "The Heat Resistant Outfit unlocks the Volcano biome — home to Blazamut, Jetragon and Legendary weapon Schematics. Skip it and you take heat damage every 10 seconds.",
    sections: [
      { heading: "Unlock and recipe", text: "Two tiers. Tier 1 at Level 14 (10 Cloth + 5 Flame Organ). Tier 2 (Refined) at Level 44 (10 Cloth + 5 Flame Organ + 10 Pal Metal Ingot)." },
      { heading: "When to wear", text: "Anywhere above 40°C ambient temperature — the entire Mount Obsidian and Volcano zone. Also great for the Anubis arena at midday." },
    ],
    faqIntro: "Heat Resistant Outfit FAQ.",
    faqs: [
      { q: "How do you get Flame Organ in Palworld?", a: "Flame Organ drops from Foxparks, Wixen, Rooby and other fire-type Pals. Send a hunting Pal or farm at the desert bonfire spawn ring." },
      { q: "Do you need Heat Resistant Armor for the Volcano in Palworld?", a: "Yes — the Volcano biome deals passive heat damage that shreds SAN and HP. Tier 2 Heat Resistant Outfit is required to farm Blazamut safely." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-cold-resistant-outfit",
    title: "Cold Resistant Outfit Guide — Palworld Gear",
    desc: "Palworld Cold Resistant Outfit recipe and best use for the Astral Mountains.",
    tag: "Gear",
    keywords: "palworld cold resistant outfit, palworld snow armor, palworld astral mountains gear",
    intro: "The Cold Resistant Outfit is required to explore the Astral Mountains and Frozen Wilderness. Without it, cold damage triggers every 5 seconds at night.",
    sections: [
      { heading: "Unlock and recipe", text: "Tier 1 at Level 11 (5 Wool + 5 Ingot + 2 Cloth). Tier 2 (Refined) at Level 44 with 10 Pal Metal Ingot added." },
      { heading: "Use case", text: "Frostallion arena, Sibelyx camps, Reindrix hunts. Also useful for the northern Sky Islands where Celesdir spawns." },
    ],
    faqIntro: "Cold Resistant Outfit FAQ.",
    faqs: [
      { q: "How do you get Wool in Palworld?", a: "Assign a Lamball to a Ranch. It produces Wool every ~2 minutes automatically." },
      { q: "Do you need Cold Resistant Armor for Frostallion in Palworld?", a: "Yes. The Frostallion arena tick-damages any player without Tier 2 cold resist." },
    ],
    links: [L.fandomArmor, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-grappling-gun",
    title: "Grappling Gun Guide — Palworld Gear",
    desc: "Palworld Grappling Gun recipe, upgrades and best traversal tricks.",
    tag: "Gear",
    keywords: "palworld grappling gun, palworld grappling hook, palworld movement gear",
    intro: "The Grappling Gun is the traversal MVP of Palworld. Scale cliffs, escape Alpha aggro and skip fall damage with a single button press.",
    sections: [
      { heading: "Unlock and recipe", text: "Basic Grappler at Level 12 (2 Paldium Fragment + 5 Fiber + 3 Ingot). Mega at Level 22, Giga at Level 42 for longer range and shorter cooldown." },
      { heading: "Best use", text: "Chain-grapple to climb cliffs faster than any mount. Combine with Ragnahawk mount for infinite-height plays." },
    ],
    faqIntro: "Grappling Gun FAQ.",
    faqs: [
      { q: "What level unlocks the Grappling Gun in Palworld?", a: "Level 12 for the basic version, Level 22 for Mega, Level 42 for Giga." },
      { q: "Does the Grappling Gun negate fall damage?", a: "Yes — grappling mid-air cancels fall velocity when you swing forward or land on a surface below." },
    ],
    links: [L.fandomItems, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-glider",
    title: "Glider Guide — Palworld Gear",
    desc: "Palworld Glider recipe, upgrades and how to reach islands without flying mounts.",
    tag: "Gear",
    keywords: "palworld glider, palworld hang glider, palworld pal glider",
    intro: "The Glider lets you jump off cliffs and cross wide gaps without a flying mount. Upgrading to the Pal Glider adds a passive-skill boost from your active Pal.",
    sections: [
      { heading: "Unlock and recipe", text: "Basic at Level 6 (5 Wood + 10 Fiber + 3 Paldium Fragment). Pal Glider at Level 24 for Pal-boosted flight speed." },
      { heading: "Best use", text: "Windswept Hills → Beach traversal, cliff escape from Alpha aggro. Pair with Grappling Gun for high-cliff base placement." },
    ],
    faqIntro: "Glider FAQ.",
    faqs: [
      { q: "What level unlocks the Glider in Palworld?", a: "Level 6 for the base Glider, Level 24 for the Pal Glider upgrade." },
      { q: "Does the Glider negate fall damage?", a: "Yes when deployed early in the fall. If you deploy under 15m from ground, some fall damage still applies." },
    ],
    links: [L.fandomItems, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-pal-sphere",
    title: "Pal Sphere Guide — Palworld Gear",
    desc: "Palworld Pal Sphere tiers, capture rates and when to use each tier.",
    tag: "Gear",
    keywords: "palworld capture rate, pal sphere gear stats, palworld sphere tiers",
    intro: "Pal Spheres are the capture tool in Palworld. Six tiers scale from starter Pal Sphere to Legendary Sphere, each with a higher catch multiplier.",
    sections: [
      { heading: "Tiers and multipliers", text: "Pal Sphere ×1, Mega Sphere ×2, Giga Sphere ×3, Hyper Sphere ×4, Ultra Sphere ×6, Legendary Sphere ×20." },
      { heading: "Recipes", text: "Pal Sphere: 1 Paldium Fragment + 1 Wood + 1 Stone. Higher tiers add Ingot, Refined Ingot, Cement and Carbon Fiber." },
      { heading: "Best use", text: "Match sphere tier to Pal level. Legendary Sphere for Alpha Jetragon; Ultra for late-game normals; Mega for anything under Level 20." },
    ],
    faqIntro: "Pal Sphere FAQ.",
    faqs: [
      { q: "How do you increase capture rate in Palworld?", a: "Lower target HP, use throwback bonuses (throw from behind = +30%), and match sphere tier to Pal level. Blunt melee weapons also raise catch odds." },
      { q: "What is the best Pal Sphere in Palworld?", a: "Legendary Sphere. It applies a ×20 catch-rate multiplier and is required for reliable Jetragon and Frostallion captures." },
    ],
    links: [L.fandomItems, L.pcgamer, L.gamerant],
  },
  {
    slug: "gear-partner-skill-necklace",
    title: "Partner Skill Necklace Guide — Palworld Gear",
    desc: "Palworld necklace slot options, drop sources and best endgame stack.",
    tag: "Gear",
    keywords: "palworld necklace, palworld accessory slot, palworld defense accessory",
    intro: "The Necklace slot on your character can hold defensive or elemental-resist accessories. The right pick can flip a Legendary fight from unwinnable to farmable.",
    sections: [
      { heading: "Best options", text: "Fire Resistant Necklace (Volcano runs), Cold Resistant Necklace (Astral Mountains), Life Necklace (+100 HP) and Musclehead Necklace (+15% ATK)." },
      { heading: "How to get", text: "Crafted at High-Grade Workbench with Cloth + elemental organ, or dropped from Syndicate lieutenants." },
    ],
    faqIntro: "Necklace FAQ.",
    faqs: [
      { q: "How many accessory slots does the player have in Palworld?", a: "Two accessory slots plus one shield and one glider slot. Mix elemental resist + defense necklaces for boss fights." },
      { q: "Best necklace for endgame in Palworld?", a: "Musclehead Necklace (+15% ATK) for offense, or Life Necklace (+100 HP) for solo Jetragon runs." },
    ],
    links: [L.fandomItems, L.pcgamer, L.gamerant],
  },
];

// ---------- Event cards (Palworld OCG) ----------
const EVENTS: ItemSpec[] = [
  {
    slug: "event-catch-bonus",
    title: "Catch Bonus Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Catch Bonus event card effect, deck slots and best synergy.",
    tag: "Event Card",
    keywords: "palworld tcg catch bonus, palworld card game event, palworld ocg deck",
    intro: "Catch Bonus is a foundational event card in the Palworld Official Card Game. It lets you attach an extra Sphere counter when playing a Pal card, ramping evolution turns.",
    sections: [
      { heading: "Card effect", text: "Play Catch Bonus during your Main Phase. Attach 1 Sphere Counter to any Pal in your zone. Discard Catch Bonus to the Event Pile." },
      { heading: "Best deck fit", text: "Neutral/Ground evolution decks running Cattiva → Anubis. Ramps you to a turn-3 Anubis." },
      { heading: "Where to get it", text: "Trial Deck: Windswept Hills (1×), Booster Pack: Palpagos Awakens (uncommon)." },
    ],
    faqIntro: "Catch Bonus event card FAQ.",
    faqs: [
      { q: "Can you play multiple Catch Bonus in one turn?", a: "Yes, but only if the second copy is played after resolving the first. Each play attaches one Sphere Counter." },
      { q: "Does Catch Bonus stack with Pal Sphere Ready?", a: "Yes. Play Pal Sphere Ready first, then Catch Bonus for a double-ramp turn." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-pal-sphere-ready",
    title: "Pal Sphere Ready Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Pal Sphere Ready effect, timing and best-in-deck use.",
    tag: "Event Card",
    keywords: "palworld tcg pal sphere ready, palworld ocg ramp card",
    intro: "Pal Sphere Ready is the most-played ramp card in the Palworld OCG. It searches your Sphere Pile and attaches one Sphere Counter to a Pal in play.",
    sections: [
      { heading: "Card effect", text: "Search your deck for 1 Sphere Counter and attach it to a Pal you control. Shuffle your deck. Discard to the Event Pile." },
      { heading: "Best use", text: "Turn-1 accelerator to power out a turn-2 Bushi or turn-3 Anubis." },
    ],
    faqIntro: "Pal Sphere Ready FAQ.",
    faqs: [
      { q: "How many Pal Sphere Ready cards can you run?", a: "The TCG allows up to 4 copies of any non-legendary event card per deck." },
      { q: "Does Pal Sphere Ready trigger 'attach' abilities?", a: "Yes. Any Pal ability that triggers on 'when a Sphere Counter is attached' resolves normally." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-tower-boss-battle",
    title: "Tower Boss Battle Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Tower Boss Battle event effect and how to win the mini-game.",
    tag: "Event Card",
    keywords: "palworld tcg tower boss battle, palworld ocg boss event",
    intro: "Tower Boss Battle triggers a mini-game where both players compete for a rewards pile. Winning nets you a random Legendary Pal from your deck to summon at half cost.",
    sections: [
      { heading: "Card effect", text: "Both players reveal the top 3 cards of their deck. Highest total power wins a Legendary summon token." },
      { heading: "Best use", text: "Run a top-heavy deck stacked with Blazamut, Necromus and Jetragon. Guarantees the Legendary token on most reveals." },
    ],
    faqIntro: "Tower Boss Battle FAQ.",
    faqs: [
      { q: "What happens on a tie in Tower Boss Battle?", a: "Both players reveal an additional card; sudden-death tiebreaker until a winner is decided." },
      { q: "Can Tower Boss Battle be countered?", a: "Yes — Emergency Retreat cancels the event if played before reveal." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-alpha-encounter",
    title: "Alpha Encounter Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Alpha Encounter event card effect and best combos.",
    tag: "Event Card",
    keywords: "palworld tcg alpha encounter, palworld ocg alpha card",
    intro: "Alpha Encounter forces a random Alpha Pal from the shared Encounter Pile onto the field. Both players fight it — the winner keeps the Alpha as a summonable Pal.",
    sections: [
      { heading: "Card effect", text: "Reveal the top card of the Alpha Encounter Pile. Both players deal power in turn order until Alpha is defeated. Winner attaches the Alpha to their Pal Zone." },
      { heading: "Best use", text: "Combat decks running Bushi, Anubis and Grizzbolt. Their high power stat lets you burst the Alpha before opponent can respond." },
    ],
    faqIntro: "Alpha Encounter FAQ.",
    faqs: [
      { q: "Can you keep the Alpha permanently in Palworld TCG?", a: "Yes — the winning player attaches the Alpha to their Pal Zone as a summonable creature for the rest of the game." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-emergency-retreat",
    title: "Emergency Retreat Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Emergency Retreat cancel-effect timing and best defensive use.",
    tag: "Event Card",
    keywords: "palworld tcg emergency retreat, palworld ocg counter card",
    intro: "Emergency Retreat is the OCG's premier counter card. Play it in response to any event or attack to negate the trigger.",
    sections: [
      { heading: "Card effect", text: "Play at any time. Counter one Event card or one attack targeting a Pal you control. Discard to the Event Pile." },
      { heading: "Best use", text: "Reactive control decks. Save for Tower Boss Battle or a lethal Bushi swing to swing tempo." },
    ],
    faqIntro: "Emergency Retreat FAQ.",
    faqs: [
      { q: "Can Emergency Retreat counter another Emergency Retreat?", a: "Yes — chain counter is allowed, resolved in reverse order (last played resolves first)." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-lucky-hatch",
    title: "Lucky Hatch Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Lucky Hatch event card effect and Lucky Pal synergy.",
    tag: "Event Card",
    keywords: "palworld tcg lucky hatch, palworld ocg lucky card, palworld shiny card",
    intro: "Lucky Hatch converts a Pal in your zone into its Lucky variant, doubling power and adding a shiny frame overlay.",
    sections: [
      { heading: "Card effect", text: "Target a Pal in your zone. Search your deck for its Lucky variant and swap them. Discard Lucky Hatch." },
      { heading: "Best use", text: "Legendary decks with Frostallion and Jetragon Lucky variants. Turns a 500-power Jetragon into a 1000-power finisher." },
    ],
    faqIntro: "Lucky Hatch FAQ.",
    faqs: [
      { q: "What is a Lucky Pal in the Palworld TCG?", a: "Lucky variants are shiny versions of standard Pals. They have doubled power stats and a distinctive gold frame." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-syndicate-raid",
    title: "Syndicate Raid Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Syndicate Raid disruption event effect and best deck.",
    tag: "Event Card",
    keywords: "palworld tcg syndicate raid, palworld ocg disruption card",
    intro: "Syndicate Raid disrupts your opponent's board by forcing them to discard 2 random cards from hand. Aggressive control staple.",
    sections: [
      { heading: "Card effect", text: "Opponent discards 2 random cards. If their hand is empty, they skip their next Draw step." },
      { heading: "Best use", text: "Aggro-control decks. Play it on turn 2 to strip opponent's ramp before they set up Anubis." },
    ],
    faqIntro: "Syndicate Raid FAQ.",
    faqs: [
      { q: "Can Syndicate Raid be countered?", a: "Yes — Emergency Retreat and Alpha Encounter's shield ability both cancel Syndicate Raid." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-breeding-farm",
    title: "Breeding Farm Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Breeding Farm event card effect and evolution combos.",
    tag: "Event Card",
    keywords: "palworld tcg breeding farm, palworld ocg breeding card",
    intro: "Breeding Farm lets you combine two Pals in your zone into a higher-tier Pal from your deck, mirroring in-game breeding.",
    sections: [
      { heading: "Card effect", text: "Send 2 Pals from your zone to the Nursery. Search your deck for a Pal whose printed Breed Power equals the average of the two sent Pals. Summon it." },
      { heading: "Best use", text: "Neutral evolution decks. Sacrifice Cattiva + Lamball for a mid-tier Bushi or Anubis reveal." },
    ],
    faqIntro: "Breeding Farm event card FAQ.",
    faqs: [
      { q: "Does Breeding Farm cost mana in the Palworld TCG?", a: "No — it uses two Pal sacrifices instead of Sphere Counters. Tempo-neutral swap." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-pal-sphere-throw",
    title: "Pal Sphere Throw Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Pal Sphere Throw effect, catch chance and best deck fit.",
    tag: "Event Card",
    keywords: "palworld tcg pal sphere throw, palworld ocg catch card",
    intro: "Pal Sphere Throw is the direct-catch event card: target a Wild Pal on the field and roll for capture based on Sphere tier used.",
    sections: [
      { heading: "Card effect", text: "Target 1 Wild Pal in the Encounter Zone. Roll a d20. On 15+, attach the Pal to your Pal Zone. On 8-14, opponent draws 1. On 1-7, discard Pal Sphere Throw." },
      { heading: "Best use", text: "Catcher decks pairing Catch Bonus + Legendary Sphere upgrade for guaranteed captures." },
    ],
    faqIntro: "Pal Sphere Throw FAQ.",
    faqs: [
      { q: "Can Pal Sphere Throw catch Legendary Pals in the TCG?", a: "Yes with a Legendary Sphere Counter attached — the roll requirement drops to 8+ for a guaranteed catch on most rolls." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
  {
    slug: "event-tower-clear-reward",
    title: "Tower Clear Reward Event Card Guide — Palworld TCG",
    desc: "Palworld OCG Tower Clear Reward event card effect and best combo lines.",
    tag: "Event Card",
    keywords: "palworld tcg tower clear reward, palworld ocg reward card",
    intro: "Tower Clear Reward gifts a random Legendary weapon or Pal card from the Reward Pile after a successful Tower Boss Battle.",
    sections: [
      { heading: "Card effect", text: "Play only after winning a Tower Boss Battle event. Reveal the top 3 cards of the Reward Pile and add 1 to your hand." },
      { heading: "Best use", text: "Chain with Tower Boss Battle in a top-heavy Legendary deck. Combines to net you 2 Legendary summons per Tower event." },
    ],
    faqIntro: "Tower Clear Reward FAQ.",
    faqs: [
      { q: "What is in the Palworld TCG Reward Pile?", a: "Reward Pile is a curated deck of Legendary weapons, Legendary Pals and Legendary Sphere Counters. Contents vary by set — check the current Trial Deck." },
    ],
    links: [L.tcgOfficial, L.tcgWiki, L.reddit],
  },
];

export const ITEM_GUIDES: Guide[] = [
  ...WEAPONS.map(itemGuide),
  ...STRUCTURES.map(itemGuide),
  ...GEAR.map(itemGuide),
  ...EVENTS.map(itemGuide),
];
